/**
 * Where a run goes next: which level a gate leads to, and which end of it the player
 * arrives at.
 *
 * Pure data in, pure data out — no coordinates, no three.js, no Rapier, no React. A
 * destination says *which* level and *which end*; where that end actually is in metres is
 * the maze's business (`buildGrounds` computes it) and never the store's. That split is
 * what keeps the rules of a run testable without a GPU, and what stops the game store
 * from growing a copy of the world.
 *
 * A run is a chain: each level's exit leads to at most one other, and at most one exit
 * leads into each level. `validate.ts` refuses anything else, so the walks below can
 * assume it.
 */

import { endlessIndex, endlessLevel, endlessLevelId } from './endless.ts';
import type { Box, GroundPoint, LevelSpec, Profile } from './profile/types.ts';

/** Which end of a level the player is standing at. */
export type ArriveAt = 'entry' | 'exit';

/**
 * Where crossing a gate puts the player: another level, or the end of the run. `null`
 * from the functions below means the gate leads nowhere at all and nothing should happen.
 */
export type Destination = { levelId: string; arriveAt: ArriveAt } | 'complete';

export function levelById(profile: Profile, levelId: string): LevelSpec | null {
  const authored = profile.levels.find((level) => level.id === levelId);
  if (authored) return authored;
  const index = endlessIndex(levelId);
  return index === null ? null : endlessLevel(profile, index);
}

/** The authored level that ends the chain (names no `exitsTo`); generated mazes follow it. */
export function lastAuthoredLevel(profile: Profile): LevelSpec | null {
  return profile.levels.find((level) => level.exitsTo === undefined) ?? null;
}

/** Where a run begins: the profile's first level, at its entry. */
export function firstLevel(profile: Profile): { levelId: string; arriveAt: ArriveAt } {
  // A profile with no levels does not parse, so there is always a first.
  return { levelId: profile.levels[0]!.id, arriveAt: 'entry' };
}

/**
 * Through the gate in a level's exit: on to the level it names, arriving at that level's
 * entry — or the end of the run, when it names none.
 */
export function crossExit(profile: Profile, fromLevelId: string): Destination | null {
  const level = levelById(profile, fromLevelId);
  if (!level) return null;
  const index = endlessIndex(fromLevelId);
  if (index !== null) return { levelId: endlessLevelId(index + 1), arriveAt: 'entry' };
  if (level.exitsTo === undefined) {
    return profile.endless === undefined
      ? 'complete'
      : { levelId: endlessLevelId(1), arriveAt: 'entry' };
  }
  if (!levelById(profile, level.exitsTo)) return null;
  return { levelId: level.exitsTo, arriveAt: 'entry' };
}

/**
 * Back through the gate in a level's entry: to whichever level leads into this one,
 * arriving outside the exit they left through. Null for the first level of a run, which
 * nothing leads into and so has no gate there to cross.
 */
export function crossEntry(profile: Profile, fromLevelId: string): Destination | null {
  if (!levelById(profile, fromLevelId)) return null;
  const index = endlessIndex(fromLevelId);
  if (index !== null) {
    if (index > 1) return { levelId: endlessLevelId(index - 1), arriveAt: 'exit' };
    const last = lastAuthoredLevel(profile);
    return last ? { levelId: last.id, arriveAt: 'exit' } : null;
  }
  const from = profile.levels.find((level) => level.exitsTo === fromLevelId);
  return from ? { levelId: from.id, arriveAt: 'exit' } : null;
}

/** How far along a run a level is: its one-based place, and the length of the chain. */
export type LevelPosition = { step: number; total: number | null };

/**
 * Where in the run this level sits. Derived from the profile's order rather than stored on
 * the level, so that adding a maze renumbers the rest for free — and so the number can
 * never disagree with the chain it describes.
 *
 * Null for a level the profile does not have, which is the same "nothing to say" the
 * lookups above return.
 */
export function levelPosition(profile: Profile, levelId: string): LevelPosition | null {
  const generated = endlessIndex(levelId);
  if (generated !== null) {
    if (profile.endless === undefined) return null;
    return { step: profile.levels.length + generated, total: null };
  }
  const index = profile.levels.findIndex((level) => level.id === levelId);
  if (index === -1) return null;
  return { step: index + 1, total: profile.endless === undefined ? profile.levels.length : null };
}

/**
 * Whether a player who has just touched a gate is leaving through it, rather than coming
 * back in. The opening runs along the gate box's wider horizontal axis, so the axis a
 * crossing actually happens on is the narrower one — that is the axis this compares.
 *
 * `inside` is always some fixed point on the maze side of the gate (its arrival point), so
 * a player on the same side of the gate's centre is leaving, and a player on the far side
 * is returning. A player exactly on the centre line — reachable only by a teleport, never
 * by walking — is neither: the safe answer to "cannot tell" is the one that does not end
 * a run.
 */
export function isLeavingThroughGate(gate: Box, inside: GroundPoint, player: GroundPoint): boolean {
  const acrossX = gate.size.x >= gate.size.z;
  const axis = acrossX ? 'z' : 'x';
  const insideSide = Math.sign(inside[axis] - gate.center[axis]);
  const playerSide = Math.sign(player[axis] - gate.center[axis]);
  return playerSide !== 0 && playerSide === insideSide;
}
